import * as React from "react";
import { ApolloError, gql, useMutation } from "@apollo/client";

import {
  MutationUpdateAlertArgs,
  UpdateAlertResponse,
} from "../../../../schemaTypes";
import { NormalizedErrorMap } from "../../../../types/NormalizedErrorMap";
import { normalizeErrors } from "../../../../utils/normalizeErrors";

type UpdateAlertMutationResult = {
  updateAlert: UpdateAlertResponse;
};

export const updateAlertMutation = gql`
  mutation UpdateAlertMutation(
    $alertId: String!
    $input: UpdateAlertInput!
  ) {
    updateAlert(
      alertId: $alertId
      input: $input
    ) {
      errors {
        path
        message
      }
      message
    }
  }
`;

interface Props {
  onMessage?: (message: string) => void;
  children: (
    data: {
      submit: (
        values: MutationUpdateAlertArgs
      ) => Promise<{
        errors: NormalizedErrorMap | null;
        message?: string;
      }>;
      loading: boolean;
      error?: ApolloError;
    }
  ) => React.ReactElement | null;
}

export const UpdateAlertController: React.FC<Props> = ({ children, onMessage }) => {
  const [updateAlert, { loading, error }] = useMutation<
    UpdateAlertMutationResult,
    MutationUpdateAlertArgs
  >(updateAlertMutation);

  const submit = async (values: MutationUpdateAlertArgs) => {
    const { data } = await updateAlert({
      variables: {
        alertId: values.alertId,
        input: values.input,
      },
    });

    const response = data?.updateAlert;
    const errors = response?.errors ? normalizeErrors(response.errors) : null;
    const message = response?.message ?? undefined;

    if (message && onMessage) {
      onMessage(message);
    }

    return {
      errors,
      message,
    };
  };

  return children({ submit, loading, error });
};
